import React, { useState, useEffect } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { Editor } from 'primereact/editor';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { commentsAPI, tasksAPI } from '../services/api';
import { Task, Comment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showCustomAlert } from './CustomAlert';

interface TaskDetailSidebarProps {
  task: Task;
  visible: boolean;
  onHide: () => void;
  onTaskUpdate: () => void;
}

const TaskDetailSidebar: React.FC<TaskDetailSidebarProps> = ({ task, visible, onHide, onTaskUpdate }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  useEffect(() => {
    if (task && visible) {
      loadComments();
    }
  }, [task?._id, visible]);

  const loadComments = async () => {
    try {
      const data = await commentsAPI.getByTask(task._id);
      setComments(data);
    } catch (err: any) {
      console.error('Error loading comments:', err);
    }
  };

  const handleAddComment = async () => {
    // Strip HTML tags to get plain text for backend, but keep formatting for display
    const textContent = newComment.replace(/<[^>]*>/g, '').trim();
    if (!textContent) return;
    try {
      await commentsAPI.create({
        text: newComment, // Send HTML formatted text
        taskId: task._id
      });
      setNewComment('');
      await loadComments();
    } catch (err: any) {
      console.error('Error adding comment:', err);
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment._id);
    // Decode HTML entities if present, otherwise use text as-is
    let textToEdit = comment.text || '';
    
    // Check if HTML is escaped and decode it
    if (textToEdit.includes('&lt;') || textToEdit.includes('&gt;') || textToEdit.includes('&amp;')) {
      // Create a temporary DOM element to decode HTML entities
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = textToEdit;
      textToEdit = tempDiv.textContent || tempDiv.innerText || textToEdit;
      
      // If that didn't work, try manual replacement
      if (textToEdit === comment.text) {
        textToEdit = textToEdit
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&#x27;/g, "'")
          .replace(/&#x2F;/g, '/');
      }
    }
    
    // If text already contains HTML tags, use it directly
    if (!textToEdit.includes('<') && comment.text.includes('<')) {
      // Text might be double-encoded, try one more time
      textToEdit = comment.text;
    }
    
    setEditingCommentText(textToEdit);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleSaveEdit = async (commentId: string) => {
    const textContent = editingCommentText.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      showCustomAlert.warning('Komentar ne može biti prazan');
      return;
    }
    try {
      await commentsAPI.update(commentId, {
        text: editingCommentText
      });
      setEditingCommentId(null);
      setEditingCommentText('');
      await loadComments();
    } catch (err: any) {
      console.error('Error updating comment:', err);
      showCustomAlert.error('Greška pri izmeni komentara');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    showCustomAlert.confirm(
      'Da li ste sigurni da želite da obrišete komentar?',
      async () => {
        try {
          await commentsAPI.delete(commentId);
          await loadComments();
        } catch (err: any) {
          console.error('Error deleting comment:', err);
          showCustomAlert.error('Greška pri brisanju komentara');
        }
      },
      'Potvrda brisanja'
    );
  };

  const statusOptions = [
    { label: 'Za rad', value: 'todo' },
    { label: 'U toku', value: 'in_progress' },
    { label: 'Završeno', value: 'done' }
  ];

  const priorityOptions = [
    { label: 'Nizak', value: 'low' },
    { label: 'Srednji', value: 'medium' },
    { label: 'Visok', value: 'high' }
  ];

  const canChangeStatus = () => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'direktor') return true;
    return (
      task.assignees?.some((assignee) => assignee._id === user._id) ||
      (task.createdBy && (typeof task.createdBy === 'object' ? task.createdBy._id : task.createdBy) === user._id)
    );
  };

  const formatCommentText = (text: string) => {
    if (!text) return { __html: '' };
    
    // Always try to decode HTML entities first (in case backend escaped them)
    let decoded = text
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#x27;/g, "'")
      .replace(/&#x2F;/g, '/');
    
    // Check if decoded text contains HTML tags
    const hasHtmlTags = /<[^>]+>/.test(decoded);
    
    if (hasHtmlTags) {
      // Text contains HTML, use it directly
      return { __html: decoded };
    }
    
    // If no HTML, apply markdown-like formatting
    // Escape HTML first
    let escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Underline: __text__
    escaped = escaped.replace(/__([^_]+?)__/g, '<u>$1</u>');
    // Bold: **text**
    escaped = escaped.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    // Italic: _text_
    escaped = escaped.replace(/_([^_]+?)_/g, '<em>$1</em>');
    // Links: http(s)://...
    escaped = escaped.replace(
      /(https?:\/\/[^\s]+)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    // Line breaks
    escaped = escaped.replace(/\n/g, '<br />');

    return { __html: escaped };
  };

  const projectName = typeof task.projectId === 'object' ? task.projectId.name : '';

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      style={{ width: '500px' }}
    >
      <div style={{ padding: '1rem' }}>
        <h2 style={{ marginTop: 0 }}>Detalji</h2>
        
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ marginBottom: '0.5rem', color: '#666', fontSize: '0.875rem' }}>
            {projectName}
          </div>
          <h3 style={{ margin: 0 }}>{task.name}</h3>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Status
          </label>
          {canChangeStatus() ? (
            <Dropdown
              value={task.status}
              options={statusOptions}
              onChange={async (e) => {
                try {
                  await tasksAPI.updateStatus(task._id, e.value);
                  onTaskUpdate();
                } catch (err) {
                  console.error('Error updating status:', err);
                }
              }}
              appendTo="self"
              style={{ width: '100%' }}
            />
          ) : (
            <Tag
              value={statusOptions.find(opt => opt.value === task.status)?.label || task.status}
              severity={task.status === 'done' ? 'success' : task.status === 'in_progress' ? 'warning' : 'info'}
            />
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Prioritet
          </label>
          {canChangeStatus() ? (
            <Dropdown
              value={task.priority}
              options={priorityOptions}
              onChange={async (e) => {
                try {
                  await tasksAPI.update(task._id, { priority: e.value });
                  onTaskUpdate();
                } catch (err) {
                  console.error('Error updating priority:', err);
                }
              }}
              appendTo="self"
              style={{ width: '100%' }}
            />
          ) : (
            <Tag
              value={priorityOptions.find(opt => opt.value === task.priority)?.label || task.priority}
              severity={task.priority === 'high' ? 'danger' : task.priority === 'medium' ? 'warning' : 'info'}
            />
          )}
        </div>

        {task.description && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Opis
            </label>
            <p style={{ margin: 0, color: '#666', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{task.description}</p>
          </div>
        )}

        <div style={{ marginBottom: '1.5rem', borderTop: '1px solid #ddd', paddingTop: '1.5rem' }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>
            Komentari ({comments.length})
          </h3>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '1rem' }}>
            {comments.map((comment) => (
              <div
                key={comment._id}
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Avatar
                      image={comment.userId.avatarUrl}
                      label={`${comment.userId.firstName[0]}${comment.userId.lastName[0]}`}
                      style={{ backgroundColor: '#9ca3af', color: 'white' }}
                      size="normal"
                      shape="circle"
                    />
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {comment.userId.firstName} {comment.userId.lastName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#666' }}>
                        {new Date(comment.createdAt).toLocaleString('sr-RS')}
                        {comment.isEdited && ' (izmenjeno)'}
                      </div>
                    </div>
                  </div>
                  {(user?._id === comment.userId._id || user?.role === 'admin' || user?.role === 'direktor') && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-text p-button-sm"
                        onClick={() => handleEditComment(comment)}
                      />
                      <Button
                        icon="pi pi-trash"
                        className="p-button-text p-button-danger p-button-sm"
                        onClick={() => handleDeleteComment(comment._id)}
                      />
                    </div>
                  )}
                </div>
                {editingCommentId === comment._id ? (
                  <div style={{ marginTop: '0.5rem' }}>
                    <Editor
                      value={editingCommentText}
                      onTextChange={(e) => setEditingCommentText(e.htmlValue || '')}
                      style={{ height: '150px', marginBottom: '0.5rem' }}
                      headerTemplate={
                        <>
                          <span className="ql-formats">
                            <button className="ql-bold" aria-label="Bold"></button>
                            <button className="ql-italic" aria-label="Italic"></button>
                            <button className="ql-underline" aria-label="Underline"></button>
                            <button className="ql-link" aria-label="Link"></button>
                          </span>
                          <span className="ql-formats">
                            <button className="ql-list" value="ordered" aria-label="Ordered List"></button>
                            <button className="ql-list" value="bullet" aria-label="Bullet List"></button>
                          </span>
                        </>
                      }
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <Button
                        label="Otkaži"
                        icon="pi pi-times"
                        className="p-button-text p-button-sm"
                        onClick={handleCancelEdit}
                      />
                      <Button
                        label="Sačuvaj"
                        icon="pi pi-check"
                        className="p-button-sm"
                        onClick={() => handleSaveEdit(comment._id)}
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    style={{ color: '#333', wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={formatCommentText(comment.text)}
                    className="comment-content"
                  />
                )}
              </div>
            ))}
          </div>

          <div>
            <Editor
              value={newComment}
              onTextChange={(e) => setNewComment(e.htmlValue || '')}
              style={{ height: '200px', marginBottom: '0.5rem' }}
              headerTemplate={
                <>
                  <span className="ql-formats">
                    <button className="ql-bold" aria-label="Bold"></button>
                    <button className="ql-italic" aria-label="Italic"></button>
                    <button className="ql-underline" aria-label="Underline"></button>
                    <button className="ql-link" aria-label="Link"></button>
                  </span>
                  <span className="ql-formats">
                    <button className="ql-list" value="ordered" aria-label="Ordered List"></button>
                    <button className="ql-list" value="bullet" aria-label="Bullet List"></button>
                  </span>
                </>
              }
            />
            <Button
              label="Dodaj"
              icon="pi pi-send"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            />
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default TaskDetailSidebar;

